import React, { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

// Shown once per login while users.password_change_due_at is set (see
// deps.password_change_state). Before the deadline: "אישור" lets her continue.
// From the deadline on the server refuses everything else, so there is no
// "אישור" and the window cannot be closed until the password is changed.
export const DISMISS_KEY = "pw_change_prompt_ok";

export function dismissedThisLogin(userId) {
  try { return sessionStorage.getItem(DISMISS_KEY) === String(userId); } catch { return false; }
}

function deadlineLabel(iso) {
  try {
    const due = new Date(iso);
    // The stored instant is midnight starting the blocking day; name that day.
    return due.toLocaleDateString("he-IL", { weekday: "long", day: "numeric", month: "numeric", timeZone: "Asia/Jerusalem" });
  } catch { return ""; }
}

export default function PasswordChangePrompt() {
  const { user, updateUser } = useAuth();
  const state = user?.password_change;
  const [dismissed, setDismissed] = useState(() => dismissedThisLogin(user?.id));
  const [changing, setChanging] = useState(false);
  // Addressed to the account's owner. An admin inside the session does not know
  // this password, and the server does not block that session over it.
  if (!state?.required || user.impersonating) return null;
  const blocking = Boolean(state.blocking);
  const show = blocking || !dismissed;

  const acknowledge = () => {
    try { sessionStorage.setItem(DISMISS_KEY, String(user.id)); } catch { /* private mode */ }
    setDismissed(true);
  };
  const changed = (data) => {
    setChanging(false);
    updateUser(data);
    toast.success("הסיסמה הוחלפה בהצלחה");
  };
  const keepOpen = (e) => e.preventDefault();

  return (
    <>
      <Dialog open={show && !changing}>
        <DialogContent dir="rtl" className="max-w-md text-right [&>button:last-child]:hidden"
                       onEscapeKeyDown={keepOpen} onPointerDownOutside={keepOpen} data-testid="pw-change-prompt">
          <div dir="rtl" className="space-y-4 text-right">
          <DialogHeader className="text-right sm:text-right">
            <DialogTitle className="flex items-center gap-2 text-red-700"><ShieldAlert size={20} /> יש להחליף סיסמה</DialogTitle>
            <DialogDescription className="text-right text-stone-700 leading-relaxed">
              {blocking
                ? "כדי להמשיך להשתמש במערכת יש להחליף את הסיסמה עכשיו."
                : <>הסיסמה הנוכחית שלך אינה בטוחה. יש להחליף אותה עד {deadlineLabel(state.due_at)}. מיום זה לא ניתן יהיה להמשיך בלי להחליף סיסמה.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button className="flex-1 text-white" style={{ background: "#1E3F33" }} onClick={() => setChanging(true)}
                    data-testid="pw-prompt-change">החלף סיסמה</Button>
            {!blocking && <Button variant="outline" className="flex-1" onClick={acknowledge} data-testid="pw-prompt-ok">אישור</Button>}
          </div>
          </div>
        </DialogContent>
      </Dialog>
      <ChangePasswordDialog open={changing} blocking={blocking}
                            onClose={() => setChanging(false)} onChanged={changed} />
    </>
  );
}
